import * as React from 'react';

import { graphql } from '@apollo/client/react/hoc';
import { Redirect } from 'react-router';

import type { Query } from 'common/apollo/types';
import EmptyView from 'common/components/core/EmptyView/EmptyView';
import GlobalLoader from 'common/components/GlobalLoader/GlobalLoader';
import { compose } from 'common/helpers/fnHelpers';
import Icons from 'common/icons';
import { AppRoutes, createRouteFromParams } from 'routes';

import AuthorizationError from './AuthorizationError/AuthorizationError';
import type { AccessResponse } from './queries';
import { getAccess } from './queries';

export type RootProps = {
  getAccess: Query<AccessResponse>;
};

/**
 * The Root component.
 *
 * The component displayed when the user navigates to the root of the project. It's responsible for
 * selecting on which cpanel section to navigate.
 */
export class Root extends React.PureComponent<RootProps> {
  private get hasAdminAccess() {
    const { access, accounts, organizations, customers } = this.props.getAccess;
    return Boolean(access?.adminAccess && (accounts?.length || organizations?.length || customers?.length));
  }

  private get hasDashboardAccess() {
    const { access, accounts } = this.props.getAccess;
    return Boolean(access?.dashboardAccess && accounts?.length);
  }

  public render() {
    const { error, loading, access } = this.props.getAccess;

    if (error) {
      return (
        <EmptyView
          iconUrl={Icons.SERVERS}
          description="The page could not be displayed. Please try again."
          fullScreen={true}
        />
      );
    }

    if (loading) {
      return <GlobalLoader />;
    }

    if (this.hasAdminAccess) {
      return <Redirect to={createRouteFromParams(AppRoutes.CLUSTER_ADMIN, {})} />;
    }

    if (this.hasDashboardAccess) {
      return <Redirect to={createRouteFromParams(AppRoutes.CLUSTER_DASHBOARD, {})} />;
    }

    if (access && !this.hasAdminAccess && !this.hasDashboardAccess) {
      return <AuthorizationError assertions={access.assertions} identityProvider={access.identityProvider} />;
    }

    return <EmptyView iconUrl={Icons.SERVERS} description="The page could not be found." fullScreen={true} />;
  }
}

export default compose<React.ComponentType<RootProps>>(
  graphql(getAccess, {
    name: 'getAccess',
    options: () => ({
      fetchPolicy: 'network-only',
    }),
  })
)(Root);
